import { inferRouteObject } from 'react-router-typed-object';
import { z } from 'zod';

import { InvoiceState, type IdAccountDto } from '@/services/ws-invoices/src/main/resources/rest-api';
import { Navigate } from '@/js/libs/router';
import { PrivateRoute, Roles, not, or, and } from '@/js/libs/use-auth';

import RouteParamsValidator from 'src/components/RouteParamsValidator';
import { useFeatureToggleContext } from 'src/modules/PostCampaign';
import { Finances } from 'src/scenes/Finances';
import { DEFAULT_PAYOUT_DATE } from 'src/scenes/Finances/constants';
import { AdditionalFilter } from 'src/scenes/Finances/containers/Payouts/containers/Reports/hooks/useFilters/types';
import { validatePayoutDate } from 'src/scenes/Finances/helpers';
import { PayoutConfigsSearchKey, PayoutWebsitesSearchKey } from 'src/scenes/Finances/types';
import PostFinances from 'src/scenes/PostFinances';
import Status from 'src/scenes/Status';

const index = true;

export const financesRouteObject = inferRouteObject({
  path: 'finances',
  element: (
    <PrivateRoute
      roles={and(or(Roles.ADMIN_INVOICE, Roles.PAYOUT, Roles.ADMIN, Roles.POSTADMANAGER, Roles.PAYMENTS), not(Roles.POSTNETZ_BRANCH_USER))}
      noAccessComponent={Status.NotFound}
    />
  ),
  children: [
    {
      Component: Finances.Helmet,
      children: [
        {
          element: (
            <useFeatureToggleContext.Provider>
              <PrivateRoute roles={Roles.POSTADMANAGER} />
            </useFeatureToggleContext.Provider>
          ),
          children: [
            {
              path: 'post-invoices',
              element: <PostFinances.Layout />,
              children: [
                {
                  path: ':state',
                  Component: PostFinances.Invoices,
                },
                {
                  index,
                  element: <Navigate to={`./${InvoiceState.OPEN}`} replace />,
                },
              ],
            },
          ],
        },
        {
          path: 'invoices',
          element: <PrivateRoute roles={not(Roles.POSTADMANAGER)} />,
          children: [
            {
              path: ':invoiceState',
              Component: Finances.Invoices,
              searchParams: z.object({
                agencyId: z.custom<IdAccountDto>().optional(),
                unpaidOnly: z.boolean().optional(),
                search: z.string().optional(),
              }).parse,
            },
            {
              index,
              element: <Navigate to={`./${InvoiceState.OPEN}`} replace />,
            },
          ],
        },
        {
          path: 'manual-invoice',
          children: [
            {
              path: ':campaignId',
              Component: Finances.ManualInvoice,
            },
            {
              index,
              element: <Navigate to={`../invoices/${InvoiceState.OPEN}`} replace />,
            },
          ],
        },
        {
          element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'company-configs',
              children: [
                { index, Component: Finances.CompanyConfigs.ConfigsList },
                { path: 'create', Component: Finances.CompanyConfigs.ConfigForm },
                { path: ':configId', Component: Finances.CompanyConfigs.ConfigForm },
              ],
            },
          ],
        },
        {
          path: 'payouts',
          element: <PrivateRoute roles={Roles.PAYOUT} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'reports',
              children: [
                {
                  path: ':date',
                  element: <RouteParamsValidator validate={validatePayoutDate} invalidStateElement={<Status.NotFound />} />,
                  children: [{ index, element: <Finances.Payouts.Report /> }],
                  searchParams: z.object({
                    [AdditionalFilter.STATE]: z.string().optional(),
                    [AdditionalFilter.PAYOUT_CONFIG]: z.string().optional(),
                  }).parse,
                },
                {
                  index,
                  element: <Navigate to={`./${DEFAULT_PAYOUT_DATE}`} replace />,
                },
              ],
            },
            {
              path: 'statistics',
              children: [
                {
                  path: ':date',
                  element: <RouteParamsValidator validate={validatePayoutDate} invalidStateElement={<Status.NotFound />} />,
                  children: [{ index, element: <Finances.Payouts.Statistics /> }],
                },
                {
                  index,
                  element: <Navigate to={`./${DEFAULT_PAYOUT_DATE}`} replace />,
                },
              ],
            },
            {
              path: 'websites',
              searchParams: z.object({
                [PayoutWebsitesSearchKey.SEARCH]: z.string().optional(),
              }).parse,
              children: [
                {
                  path: ':id',
                  element: <Finances.Payouts.WebsiteForm />,
                },
                {
                  path: 'create',
                  element: <Finances.Payouts.WebsiteForm />,
                },
                {
                  path: 'create/:cloneId',
                  element: <Finances.Payouts.WebsiteForm />,
                },
                {
                  index,
                  element: <Finances.Payouts.Websites />,
                },
              ],
            },
            {
              path: 'payout-configs',
              searchParams: z.object({
                [PayoutConfigsSearchKey.SEARCH]: z.string().optional(),
              }).parse,
              children: [
                {
                  path: ':id',
                  element: <Finances.Payouts.PayoutConfigForm />,
                },
                {
                  path: 'create',
                  element: <Finances.Payouts.PayoutConfigForm />,
                },
                {
                  path: 'create/:cloneId',
                  element: <Finances.Payouts.PayoutConfigForm />,
                },
                {
                  index,
                  element: <Finances.Payouts.PayoutConfigs />,
                },
              ],
            },
            {
              path: 'positions',
              children: [
                {
                  path: ':date',
                  element: <RouteParamsValidator validate={validatePayoutDate} invalidStateElement={<Status.NotFound />} />,
                  children: [{ index, element: <Finances.Payouts.Positions /> }],
                },
                {
                  index,
                  element: <Navigate to={`./${DEFAULT_PAYOUT_DATE}`} replace />,
                },
              ],
            },
            {
              path: 'details',
              children: [
                {
                  path: ':date',
                  element: <RouteParamsValidator validate={validatePayoutDate} invalidStateElement={<Status.NotFound />} />,
                  children: [{ index, element: <Finances.Payouts.Details /> }],
                },
                {
                  index,
                  element: <Navigate to={`./${DEFAULT_PAYOUT_DATE}`} replace />,
                },
              ],
            },
            {
              index,
              element: <Navigate to={`./reports/${DEFAULT_PAYOUT_DATE}`} replace />,
            },
          ],
        },
        {
          element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'credit-card-receipts',
              Component: Finances.CreditCardReceipts.Layout,
              children: [
                { index, element: <Navigate to='./payments' replace /> },
                { path: 'payments', Component: Finances.CreditCardReceipts.Payments },
                { path: 'payouts', Component: Finances.CreditCardReceipts.Payouts },
              ],
            },
          ],
        },
        {
          element: <PrivateRoute roles={Roles.PAYMENTS} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'payments',
              Component: Finances.Payments,
            },
          ],
        },
        { index, element: <Navigate to={`./invoices/${InvoiceState.OPEN}`} replace /> },
      ],
    },
    {
      path: 'booking-periods',
      element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
      children: [{ index, Component: Finances.BookingPeriods }],
    },
  ],
});
